/**
 * usePermissions
 *
 * Reactive composable that exposes the current user's permissions within the
 * active tenant.  All values are Vue `ComputedRef<boolean>` so templates and
 * watchers automatically update whenever the user's role changes.
 *
 * Usage:
 *   const { canInviteMembers, canMutate } = usePermissions();
 */

import { PERMISSIONS } from '~/lib/permissions';

export function usePermissions() {
    const tenantStore = useTenantStore();

    const role = computed(() => tenantStore.currentTenantRole);
    const subscription = useWorkspaceSubscription();
    const workspaceWritable = computed(() => subscription.entitlements.value.accessMode === 'normal');

    return {
        /** The current user's raw role string (owner | admin | member | viewer | null). */
        role,

        /** True when the user may invite new team members (admin+). */
        canInviteMembers: computed(() => workspaceWritable.value && PERMISSIONS.canInviteMembers(role.value)),

        /** True when the user may remove existing team members (admin+). */
        canRemoveMembers: computed(() => workspaceWritable.value && PERMISSIONS.canRemoveMembers(role.value)),

        /** True when the user may change a team member's role (admin+). */
        canManageRoles: computed(() => workspaceWritable.value && PERMISSIONS.canManageRoles(role.value)),

        /** True when the user may create, edit, or delete content (member+). */
        canMutate: computed(() => workspaceWritable.value && PERMISSIONS.canMutate(role.value)),

        /** True when the user has full team-management access (admin+). */
        canManageTeam: computed(() => workspaceWritable.value && PERMISSIONS.canManageTeam(role.value)),

        /** True when the user may approve/reject approval requests (viewer+). */
        canRespondApprovals: computed(() => workspaceWritable.value && PERMISSIONS.canRespondApprovals(role.value)),

        /** True when the user may send tenant/client messages (viewer+). */
        canMessage: computed(() => workspaceWritable.value && PERMISSIONS.canMessage(role.value)),
        workspaceAccessMode: computed(() => subscription.entitlements.value.accessMode),
    };
}
